import React, {useEffect, useState} from 'react';
import { Button, Checkbox, FormControl, Select, FormGroup, Slider, Stack, TextField, Typography, FormControlLabel, MenuItem, Grid2 as Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { OrcaPositionInterface, PositionSettingsInterface } from './types';
import { displayWithNonZeroDecimals } from '../../libs/utils';
import CurrencySelect from './CurrencySelect';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';

interface PSProps {
    posInfo?: OrcaPositionInterface;
    open: boolean;
    onClose: () => void;
    onApplySettings: (settings: PositionSettingsInterface) => void;
}

export const defaultPositionSettings: PositionSettingsInterface = {
    positionMint: '',
    autoCollect: true, 
    autoRebalance: true, 
    feesToRebalance: false,
    nextRangeSize: 0,
    depositPercent: 50,
    maxDepositAmount: 0,
    stopCheck: false,
    stopBelowA: 0,
    stopBelowB: 0,
    stopConvertCurrency: '',
    harvestCurrency: '',
    rebalanceNow: false,
    isUsed: false,
    usePreserveStrategy: false,
    maintainCoin: '',
    maintainAmountA: 0,
    maintainAmountB: 0,
    triggerSlippage: 0,
    minimumCheckMinutes: 0,
};

const numberFields = ["depositPercent", "nextRangeSize", "maxDepositAmount", "stopBelowA", "stopBelowB", "triggerSlippage", "minimumCheckMinutes", "maintainAmountA", "maintainAmountB"]

export default function PSModal({ open, onClose, posInfo, onApplySettings}: PSProps) {
    const [settings, setSettings] = useState<PositionSettingsInterface>(defaultPositionSettings);
    const prices = useSelector((state: RootState) => state.prices);

    const handleClose = () => onClose();
    const handleChangeSettings = (key: keyof PositionSettingsInterface, value: number | number[] | string | boolean) => {
        if (Array.isArray(value)) {
            return;
        }
        if (numberFields.includes(key as string)) {
            const numValue = Number(value);
            if (!isNaN(numValue)) {
                setSettings({...settings, [key]: value});
            }
        } else {
            setSettings({...settings, [key]: value});
        }
    };

    const handleApply = () => {
        const temp = {...settings, ...(settings.usePreserveStrategy && {
            maintainAmountA: settings.maintainAmountA || Number(displayWithNonZeroDecimals(posInfo?.amountA, 2)),
            maintainAmountB: settings.maintainAmountB || Number(displayWithNonZeroDecimals(posInfo?.amountB, 2)), 
            triggerSlippage: settings.triggerSlippage || 0,
            minimumCheckMinutes: settings.minimumCheckMinutes || 5
        })};
        onApplySettings(temp);
        onClose();
    }

    useEffect(() => {
        if(posInfo?.settings){
            const tempSettings = {
                ...defaultPositionSettings, 
                ...posInfo.settings,
                maintainCoin: posInfo.settings?.maintainCoin || posInfo.symbolA,
                maintainAmountA: posInfo.settings?.maintainAmountA || Number(displayWithNonZeroDecimals(posInfo.amountA, 2)),
                maintainAmountB: posInfo.settings?.maintainAmountB || Number(displayWithNonZeroDecimals(posInfo.amountB, 2)),
                triggerSlippage: posInfo.settings?.triggerSlippage || 0,
                minimumCheckMinutes: posInfo.settings?.minimumCheckMinutes || 5
            }
            setSettings(tempSettings);
        }
    }, [posInfo])

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                    width: '100vw', maxWidth: '550px', height: 'auto', maxHeight: '100vh', overflowY: 'auto',
                    position: 'absolute',
                    top: '50%',
                    right: '20%',
                    transform: 'translate(0, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    px: 2,
                    py: 2,
                }}>
                    <Typography variant='h6'>{posInfo?.symbolA}/{posInfo?.symbolB}</Typography>
                    <Typography sx={{my: 0.5}}>Current Range Size</Typography>
                    <Typography  variant='body1'>{displayWithNonZeroDecimals(posInfo?.rangeWidth, 1)}</Typography>
                    <Typography sx={{my: 0.5}}>Next Range Size Desired</Typography>
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                        <TextField size='small' value={settings.nextRangeSize} sx={{width: '100px'}} onChange={(e)=>handleChangeSettings('nextRangeSize', e.target.value)}/>
                    </Box>
                    <Typography sx={{mt: 4, mb: 0.5}}>Weighted Rebalance Adjustor</Typography>
                    <Slider value={settings.depositPercent} valueLabelDisplay="auto"  min={10} max={90} onChange={(event, newValue)=>handleChangeSettings('depositPercent', newValue)}/>
                    <Stack flexDirection='row' gap={4} mt={0.5}>
                        <Stack>
                            <Typography>{posInfo?.symbolA}</Typography>
                            <TextField size='small' value={ `${settings.depositPercent}%`} disabled/>
                        </Stack>
                        <Stack>
                            <Typography>{posInfo?.symbolB}</Typography>
                            <TextField size='small' value={ `${100 - settings.depositPercent}%`}  disabled/>
                        </Stack>
                    </Stack>
                    <Typography sx={{mt: 3}}>Maximum Iteration Amount</Typography>
                    <Typography variant='body2'>Enter the maximum amount of money in USD terms that should be deposited into a new pool position over and
                        over until all funds are deposited. If set to 0 then 70% of the funds will be desposited followed by the rest.
                    </Typography>
                    <Box  sx={{display: 'flex', alignItems: 'center', mt: 1}}>
                        {"<"} <TextField size='small' value={settings.maxDepositAmount} sx={{mx: 2}} onChange={(e)=>handleChangeSettings('maxDepositAmount', e.target.value)}/> USD
                    </Box>
                    <Box sx={{display: 'flex', mt: 4, justifyContent: 'space-between'}}>
                        <Box sx={{mr: 2, mt: 0.8}}>
                            <Checkbox checked={settings.stopCheck} sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} onChange={(e)=>handleChangeSettings('stopCheck', e.target.checked)} />
                        </Box>
                        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                            <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center' , mt: 1}}>
                                <Typography variant='body1'>Stop Pool if {posInfo?.symbolA} below</Typography>
                                <TextField size='small' value={settings.stopBelowA} sx={{mx: 2, width: '150px'}} onChange={(e)=>handleChangeSettings('stopBelowA', e.target.value)}/>
                            </Box>
                            <Box sx={{width: '100%', mt: 1}}>
                                <Typography variant='body2' sx={{color: 'text.disabled'}}>Current Value of {posInfo?.symbolA} / USDC: {posInfo?.symbolA ? displayWithNonZeroDecimals(prices[posInfo?.symbolA], 1) : 'N/A'}</Typography>
                            </Box>
                            <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1}}>
                                <Typography>Or {posInfo?.symbolB} below</Typography>
                                <TextField size='small' value={settings.stopBelowB} sx={{mx: 2, width: '150px'}} onChange={(e)=>handleChangeSettings('stopBelowB', e.target.value)}/>
                            </Box>
                            <Box sx={{width: '100%', mt: 1}}>
                                <Typography variant='body2' sx={{color: 'text.disabled'}}>Current Value of {posInfo?.symbolB} / USDC: {posInfo?.symbolB ? displayWithNonZeroDecimals(prices[posInfo?.symbolB], 1) : 'N/A'}</Typography>
                            </Box>
                            <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2}}>
                                <Typography>If stop, convert all currency to</Typography>
                                <CurrencySelect value={settings.stopConvertCurrency} sx={{mx: 2, width: '150px'}} onChange={(e)=>handleChangeSettings('stopConvertCurrency', e.target.value)}/>
                            </Box>

                        </Box>
                    </Box>
                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 3, width: '100%'}}>
                        <Typography sx={{ml: 6}}>Harvest Currency</Typography>
                        <CurrencySelect value={settings.harvestCurrency} sx={{ml: 2, width: '200px'}} onChange={(e)=>handleChangeSettings('harvestCurrency', e.target.value)}/>
                    </Box>

                    <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center'}} >
                        <FormGroup>
                            <FormControlLabel control={<Checkbox checked={settings.usePreserveStrategy} sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} onChange={(e)=>handleChangeSettings('usePreserveStrategy', e.target.checked)} />} label="Use Preserve Equity Strategy" />
                        </FormGroup>
                        <Grid container rowSpacing={0.5} columnSpacing={1}>
                            <Grid size={6} sx={{display: 'flex', alignItems: 'center', justifyContent: 'right'}}>
                                <Typography>Maintain Coin: </Typography>
                            </Grid>
                            <Grid size={6}>
                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                    <FormControl sx={{ minWidth: 120}} size="small">
                                        <Select value={settings.maintainCoin} onChange={(e)=>handleChangeSettings('maintainCoin', e.target.value)} displayEmpty inputProps={{ 'aria-label': 'Without label' }}>
                                            <MenuItem value={posInfo?.symbolA} sx={{height: '30px'}}>{posInfo?.symbolA}</MenuItem>
                                            <MenuItem value={posInfo?.symbolB} sx={{height: '30px'}}>{posInfo?.symbolB}</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Typography color='divider'>{posInfo?.settings?.maintainCoin}</Typography>
                                </Stack>
                            </Grid>
                            <Grid size={6} sx={{display: 'flex', alignItems: 'center', justifyContent: 'right'}}>
                                <Typography>{settings.maintainCoin === posInfo?.symbolA && "Maintain Amount"} {posInfo?.symbolA}:</Typography>
                            </Grid>
                            <Grid size={6}>
                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                    <TextField size='small' value={settings.maintainAmountA} sx={{width: '150px'}} onChange={(e)=>handleChangeSettings('maintainAmountA', e.target.value)}/>
                                    <Typography color='divider'>{posInfo?.settings?.maintainAmountA}</Typography>
                                </Stack>
                            </Grid>
                            
                            <Grid size={6} sx={{display: 'flex', alignItems: 'center', justifyContent: 'right'}}>
                                <Typography>{settings.maintainCoin === posInfo?.symbolB && "Maintain Amount"} {posInfo?.symbolB}:</Typography>
                            </Grid>
                            <Grid size={6}>
                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                    <TextField size='small' value={settings.maintainAmountB} sx={{width: '150px'}} onChange={(e)=>handleChangeSettings('maintainAmountB', e.target.value)}/>
                                    <Typography color='divider'>{posInfo?.settings?.maintainAmountB}</Typography>
                                </Stack>
                            </Grid>

                            <Grid size={6} sx={{display: 'flex', alignItems: 'center', justifyContent: 'right', mt: 1}}>
                                <Typography>Trigger Slippage: </Typography>
                            </Grid>
                            <Grid size={6} sx={{mt: 1}}>
                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                    <TextField size='small' value={settings.triggerSlippage} sx={{width: '150px'}} onChange={(e)=>handleChangeSettings('triggerSlippage', e.target.value)}/>
                                    <Typography color='divider'>{posInfo?.settings?.triggerSlippage}</Typography>
                                </Stack>
                            </Grid>
                            <Grid size={6} sx={{display: 'flex', alignItems: 'center', justifyContent: 'right'}}>
                                <Typography>Minimum Check Minutes: </Typography>
                            </Grid>
                            <Grid size={6}>
                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                    <TextField size='small' value={settings.minimumCheckMinutes} sx={{width: '150px'}} onChange={(e)=>handleChangeSettings('minimumCheckMinutes', e.target.value)}/>
                                    <Typography color='divider'>{posInfo?.settings?.minimumCheckMinutes}</Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center'}} >
                        <FormGroup>
                            <FormControlLabel control={<Checkbox checked={settings.rebalanceNow} sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} onChange={(e)=>handleChangeSettings('rebalanceNow', e.target.checked)} />} label="Rebalance Now" />
                        </FormGroup>
                        <Typography variant='body2'>
                            Check this box to perform a rebalance when you apply settings. Otherwise the settings will be applied on the next rebalance.
                        </Typography>
                    </Box>
                    <Stack flexDirection={'row'} gap={6} mt={2}>
                        <Button variant='outlined'  onClick={()=>handleApply()}>Apply Settings</Button>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
}
